// The Dashboard's "Details" tab (owner 2026-10-31): everything the database knows about the player's wins, counted by
// the server (stats/get.json?target=details, server/lib/playerDetails.js) and drawn with our own SVG charts
// (ui/svgCharts.tsx). Loaded the first time the tab is shown, in the browser's time zone, so days and hours are the
// player's own.
import { useCallback, useEffect, useState } from 'react';
import { get } from '../api/client';
import { env } from '../env';
import { variantOf } from '../engine/gameRegistry';
import { currentLanguage, langTag, t } from '../i18n';
import { PlayerFlag } from '../online/PlayerName';
import { withIndicator } from '../ui/busy';
import { Bars, Donut, HBars, Meter, OTHER_COLOR, SERIES, Tile, toSlices, useOnce, type Bar } from '../ui/svgCharts';
import { formatWinTime } from './WinWindow';

interface Stamp {
  at: number;
  text: string;
}
interface Best {
  value: number;
  id: string;
  created: Stamp;
}
interface GameRow {
  game_type: string;
  wins: number;
  share: number;
  new_deals: number;
  retry_deals: number;
  avg_time: number | null;
  avg_score: number | null;
  avg_moves: number | null;
  sum_time: number;
  first_win: Stamp | null;
  last_win: Stamp | null;
  best_time: Best | null;
  best_score: Best | null;
  best_moves: Best | null;
  rank: { players: number | null; score?: number; time?: number; moves?: number } | null;
}
interface RecentRow {
  id: string;
  game_type: string;
  scoring_type: string;
  time: number;
  score: number;
  moves: number;
  is_retry_deal: number;
  created: Stamp;
}
export interface Details {
  tz: string;
  today: string;
  totals: {
    wins: number;
    first_win: Stamp | null;
    last_win: Stamp | null;
    days: number;
    sum_time: number;
    avg_time: number | null;
    median_time: number | null;
    sum_moves: number;
    sum_score: number;
    best_score: number | null;
    best_time: number | null;
    new_deals: number;
    retry_deals: number;
    timed: number;
    standard: number;
  };
  streak: { longest: number; longest_from: string | null; longest_to: string | null; current: number };
  games: GameRow[];
  months: Array<{ month: string; wins: number }>;
  weekdays: number[];
  hours: number[];
  variants: Record<string, Array<{ variant: string; wins: number }>>;
  recent: RecentRow[];
}

/** How many months the month chart shows (the older ones are in the years chart). */
export const MONTHS_SHOWN = 24;

const gameName = (id: string): string => t(`win.${id}`) || id;
const browserZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** "12 h 34 min" from seconds; under an hour "34 min". */
export function hoursMinutes(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? t('details.hours-minutes', { h, m }) : t('details.minutes', { m });
}

const monthLabel = (ym: string, long: boolean): string => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, 1));
  try {
    return new Intl.DateTimeFormat(langTag(currentLanguage()), {
      month: long ? 'long' : 'short',
      year: long ? 'numeric' : undefined,
      timeZone: 'UTC',
    }).format(d);
  } catch {
    return ym;
  }
};
const weekdayLabel = (iso: number, long: boolean): string => {
  const d = new Date(Date.UTC(2024, 0, iso)); // 2024-01-01 is a Monday
  try {
    return new Intl.DateTimeFormat(langTag(currentLanguage()), {
      weekday: long ? 'long' : 'short',
      timeZone: 'UTC',
    }).format(d);
  } catch {
    return String(iso);
  }
};

/** The last MONTHS_SHOWN months up to `today`, gaps filled with zero. */
export function lastMonths(months: Array<{ month: string; wins: number }>, today: string): Bar[] {
  const have = new Map(months.map((m) => [m.month, m.wins]));
  const [y, m] = today.split('-').map(Number);
  const out: Bar[] = [];
  for (let i = MONTHS_SHOWN - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1 - i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const wins = have.get(key) ?? 0;
    out.push({
      key,
      label:
        d.getUTCMonth() === 0 || i === MONTHS_SHOWN - 1
          ? `${monthLabel(key, false)} ${d.getUTCFullYear()}`
          : monthLabel(key, false),
      value: wins,
      title: `${monthLabel(key, true)}: ${wins}`,
    });
  }
  return out;
}

/** Wins per year, every year from the first to the last. */
export function years(months: Array<{ month: string; wins: number }>): Bar[] {
  const sums = new Map<number, number>();
  for (const m of months) {
    const y = Number(m.month.slice(0, 4));
    sums.set(y, (sums.get(y) ?? 0) + m.wins);
  }
  const ys = [...sums.keys()];
  if (!ys.length) return [];
  const out: Bar[] = [];
  for (let y = Math.min(...ys); y <= Math.max(...ys); y++)
    out.push({ key: String(y), label: String(y), value: sums.get(y) ?? 0 });
  return out;
}

const share = (id: string) => `${env.fullAddr}my-score/${id}.html`;

interface OnlineGame {
  at: Stamp;
  variant: string;
  seconds: number;
  result: 'win' | 'loss' | 'draw' | 'left';
  score: number | null;
  private: boolean;
  others: Array<{ bot: true; score: number | null; win?: boolean } | { name: string; flag?: string; score: number | null; win?: boolean }>;
}
interface OnlineData {
  totals: { games: number; win: number; loss: number; draw: number; left: number; seconds: number };
  games: Array<{ variant: string; games: number; win: number }>;
  opponents: Array<{ name: string; flag?: string; games: number; win: number; loss: number }>;
  recent: OnlineGame[];
}

/** The online activity (owner 2026-11-01): the games from mp_games, loaded when this sub-tab is first shown. */
function OnlineActivity({ gameName, colorOf, quiet }: { gameName: (id: string) => string; colorOf?: ReadonlyMap<string, string>; quiet?: boolean }) {
  const [data, setData] = useState<OnlineData | null | undefined>(undefined);
  useEffect(() => {
    void withIndicator(() => get('stats/get.json', { target: 'online' })).then((r) => {
      setData(r.result === 'ok' && r.response && typeof r.response === 'object' ? (r.response as OnlineData) : null);
    });
  }, []);
  // `quiet`: under the note of a player with no solo win: only when there is something to show
  if (data === undefined) return quiet ? null : <p className="details_note">{`${t('game.loading')}...`}</p>;
  if (!data) return quiet ? null : <p className="details_note">{t('details.failed')}</p>;
  if (!data.totals.games) return quiet ? null : <p className="details_note">{t('details.online.empty')}</p>;
  const { totals } = data;
  // the dots of the other sections: a game keeps its colour, one played only online takes a colour still free
  const taken = new Set(colorOf?.values());
  const free = SERIES.filter((c) => !taken.has(c));
  const dots = new Map(colorOf);
  for (const g of data.games) if (!dots.has(g.variant)) dots.set(g.variant, free.shift() ?? OTHER_COLOR);
  const dot = (variant: string) => <i className="details_dot" style={{ background: dots.get(variant) ?? OTHER_COLOR }} />;
  return (
    <>
      <div className="details_tiles" data-online="totals">
        <Tile label={t('details.online.games')} value={totals.games.toLocaleString()} />
        <Tile label={t('details.tile.wins')} value={totals.win.toLocaleString()} />
        <Tile label={t('details.online.losses')} value={totals.loss.toLocaleString()} />
        <Tile label={t('details.col.time')} value={hoursMinutes(totals.seconds)} />
      </div>
      <section className="details_box details_table_box">
        <h3>{t('details.online.head-recent')}</h3>
        <div className="details_table_scroll">
          <table className="details_table" data-table="online-recent">
            <thead>
              <tr>
                <th>{t('details.col.when')}</th>
                <th>{t('details.col.game')}</th>
                <th>{t('details.online.col-result')}</th>
                <th>{t('details.online.col-with')}</th>
                <th className="num">{t('details.online.col-you')}</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((g, i) => (
                <tr key={i} data-result={g.result}>
                  <td>{g.at.text}</td>
                  <td>
                    {dot(g.variant)}
                    {gameName(g.variant)}
                  </td>
                  {/* beside the game: on a phone the table's right side is a swipe away */}
                  <td className={`details_result details_result_${g.result}`}>{t(`details.online.result-${g.result}`)}</td>
                  <td>
                    {/* one tag per opponent, side by side: who, their points, the winner marked (owner: "schodzi jedna na linię") */}
                    <div className="details_with">
                      {g.others.map((p, k) => (
                        <span key={k} className={`details_person${'name' in p ? '' : ' details_person_bot'}${p.win ? ' details_person_win' : ''}`}>
                          {'name' in p ? (
                            <>
                              <PlayerFlag flag={p.flag} />
                              {p.name}
                            </>
                          ) : (
                            t('details.online.bot')
                          )}
                          {p.score !== null ? <b>{p.score.toLocaleString()}</b> : null}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="num" data-label={t('details.online.col-you')}>{g.score === null ? '-' : g.score.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <div className="details_grid">
        {data.opponents.length ? (
          <section className="details_box details_table_box">
            <h3>{t('details.online.head-people')}</h3>
            <div className="details_table_scroll">
              <table className="details_table" data-table="online-people">
                <thead>
                  <tr>
                    <th>{t('details.online.col-with')}</th>
                    <th className="num">{t('details.online.games')}</th>
                    <th className="num">{t('details.tile.wins')}</th>
                    <th className="num">{t('details.online.losses')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.opponents.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <PlayerFlag flag={p.flag} />
                        {p.name}
                      </td>
                      <td className="num">{p.games}</td>
                      <td className="num">{p.win}</td>
                      <td className="num">{p.loss}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
        <section className="details_box details_table_box">
          <h3>{t('details.head.games')}</h3>
          <div className="details_table_scroll">
            <table className="details_table" data-table="online-games">
              <thead>
                <tr>
                  <th>{t('details.col.game')}</th>
                  <th className="num">{t('details.online.games')}</th>
                  <th className="num">{t('details.tile.wins')}</th>
                </tr>
              </thead>
              <tbody>
                {data.games.map((g) => (
                  <tr key={g.variant}>
                    <td>
                      {dot(g.variant)}
                      {gameName(g.variant)}
                    </td>
                    <td className="num">{g.games}</td>
                    <td className="num">{g.win}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}

export const SECTIONS = ['overview', 'history', 'games', 'recent', 'online'] as const;
export type Section = (typeof SECTIONS)[number];

export function DetailsPanel({ shown }: { shown: boolean }) {
  // undefined = not loaded yet, null = the request failed
  const [data, setData] = useState<Details | null | undefined>(undefined);
  const [state, setState] = useState<'idle' | 'loading' | 'done'>('idle');
  const load = useCallback(() => {
    setState('loading');
    void withIndicator(() => get('stats/get.json', { target: 'details', tz: browserZone() })).then((r) => {
      setData(
        r.result === 'ok' && r.response && typeof r.response === 'object' && !Array.isArray(r.response)
          ? (r.response as Details)
          : null,
      );
      setState('done');
    });
  }, []);
  useOnce(shown, load);
  // four short sections picked with pills (owner 2026-10-31: one long page "za długie, nikt nie ma takiego ekranu")
  const [section, setSection] = useState<Section>('overview');

  if (state !== 'done') return <div className="details_panel details_note">{`${t('game.loading')}...`}</div>;
  if (!data) return <div className="details_panel details_note">{t('details.failed')}</div>;
  // no solo win yet, but maybe online games: only the online part then
  if (!data.totals.wins) {
    return (
      <div className="details_panel" data-section="online">
        <div className="details_body">
          <p className="details_note">{t('details.empty')}</p>
          <OnlineActivity gameName={(id) => t(id)} quiet />
        </div>
      </div>
    );
  }

  const { totals, streak, games } = data;
  const colorOf = new Map(
    toSlices(
      games.map((g) => ({ key: g.game_type, label: gameName(g.game_type), value: g.wins })),
      t('details.other'),
    ).map((s) => [s.key, s.color]),
  );
  const monthBars = lastMonths(data.months, data.today);
  const yearBars = years(data.months);
  const weekdayBars: Bar[] = data.weekdays.map((v, i) => ({
    key: String(i + 1),
    label: weekdayLabel(i + 1, false),
    value: v,
    title: `${weekdayLabel(i + 1, true)}: ${v}`,
  }));
  const hourBars: Bar[] = data.hours.map((v, i) => ({
    key: String(i),
    label: String(i),
    value: v,
    title: `${String(i).padStart(2, '0')}:00 - ${String(i).padStart(2, '0')}:59: ${v}`,
  }));
  const place = (g: GameRow, metric: 'score' | 'time' | 'moves') => {
    const p = g.rank?.[metric];
    if (!p) return null;
    return (
      <span className="details_place" title={t(`details.place.${metric}`)}>
        {g.rank?.players ? t('details.place', { place: p, players: g.rank.players }) : String(p)}
      </span>
    );
  };
  const bestCell = (b: Best | null, text: (v: number) => string) =>
    b ? (
      <a href={share(b.id)} target="_blank" rel="noreferrer" title={b.created.text}>
        {text(b.value)}
      </a>
    ) : (
      '-'
    );

  const pills = (
    <div className="details_pills games_sort" role="group">
      {SECTIONS.map((sec) => (
        <button
          key={sec}
          type="button"
          className={`games_sort_btn${section === sec ? ' on' : ''}`}
          data-section={sec}
          aria-pressed={section === sec}
          onClick={() => setSection(sec)}
        >
          {t(`details.section.${sec}`)}
        </button>
      ))}
    </div>
  );

  const overview = (
    <>
      <div className="details_tiles">
        <Tile
          value={totals.wins.toLocaleString()}
          label={t('details.tile.wins')}
          note={totals.first_win ? t('details.tile.since', { date: totals.first_win.text.slice(0, 10) }) : undefined}
        />
        <Tile
          value={totals.days.toLocaleString()}
          label={t('details.tile.days')}
          note={totals.last_win ? t('details.tile.last', { date: totals.last_win.text.slice(0, 10) }) : undefined}
        />
        <Tile value={hoursMinutes(totals.sum_time)} label={t('details.tile.time')} />
        <Tile
          value={formatWinTime(totals.median_time ?? 0)}
          label={t('details.tile.median-time')}
          note={t('details.tile.avg-time', { time: formatWinTime(totals.avg_time ?? 0) })}
        />
        <Tile
          value={streak.longest.toLocaleString()}
          label={t('details.tile.streak')}
          note={
            streak.longest_from && streak.longest_to && streak.longest > 1
              ? `${streak.longest_from} - ${streak.longest_to}`
              : undefined
          }
        />
        <Tile value={streak.current.toLocaleString()} label={t('details.tile.current-streak')} />
        <Tile value={totals.sum_moves.toLocaleString()} label={t('details.tile.moves')} />
        <Tile
          value={totals.sum_score.toLocaleString()}
          label={t('details.tile.score')}
          note={totals.best_score !== null ? t('details.tile.best-score', { score: totals.best_score }) : undefined}
        />
      </div>
      <div className="details_grid">
        <section className="details_box">
          <h3>{t('details.head.share')}</h3>
          <div className="details_donut">
            <Donut
              slices={toSlices(
                games.map((g) => ({ key: g.game_type, label: gameName(g.game_type), value: g.wins })),
                t('details.other'),
              )}
              total={totals.wins}
              totalLabel={t('details.tile.wins')}
            />
          </div>
        </section>
        <section className="details_box">
          <h3>{t('details.head.mix')}</h3>
          <Meter
            a={{ label: t('details.mix.new'), value: totals.new_deals }}
            b={{ label: t('details.mix.retry'), value: totals.retry_deals }}
          />
          <Meter
            className="details_meter2"
            a={{ label: t('details.mix.standard'), value: totals.standard }}
            b={{ label: t('details.mix.timed'), value: totals.timed }}
          />
          {Object.entries(data.variants).map(([game, rows]) => (
            <div key={game} className="details_variants">
              <h4>
                {gameName(game)}: {t(variantOf(game)?.labelKey ?? '')}
              </h4>
              <HBars
                bars={rows.map((r) => ({
                  key: r.variant,
                  label: t(variantOf(game)?.nameKey(r.variant) ?? '') || r.variant,
                  value: r.wins,
                }))}
                color={colorOf.get(game) ?? SERIES[0]}
              />
            </div>
          ))}
        </section>
      </div>
    </>
  );

  const history = (
    <div className="details_grid">
      <section className="details_box details_wide">
        <h3>{t('details.head.history')}</h3>
        <p className="details_sub">{t('details.months-note')}</p>
        <Bars bars={monthBars} height={190} labelEvery={2} />
      </section>
      {yearBars.length > 1 ? (
        <section className="details_box">
          <h3>{t('details.head.years')}</h3>
          <Bars bars={yearBars} height={160} color={SERIES[2]} />
        </section>
      ) : null}
      <section className={`details_box${yearBars.length > 1 ? '' : ' details_wide'}`}>
        <h3>{t('details.head.weekdays')}</h3>
        <Bars bars={weekdayBars} height={160} color={SERIES[6]} />
      </section>
      <section className="details_box details_wide">
        <h3>{t('details.head.hours')}</h3>
        <p className="details_sub">{t('details.tz-note', { tz: data.tz.replace(/_/g, ' ') })}</p>
        <Bars bars={hourBars} height={160} color={SERIES[1]} labelEvery={2} />
      </section>
    </div>
  );

  const gamesSection = (
    <>
      <section className="details_box details_table_box">
        <h3>{t('details.head.games')}</h3>
        <div className="details_table_scroll">
          <table className="details_table" data-table="games">
            <thead>
              <tr>
                <th>{t('details.col.game')}</th>
                <th className="num">{t('details.col.wins')}</th>
                <th className="num">{t('details.col.share')}</th>
                <th className="num">{t('details.col.best-time')}</th>
                <th className="num">{t('details.col.best-score')}</th>
                <th className="num">{t('details.col.best-moves')}</th>
                <th className="num">{t('details.col.avg-time')}</th>
                <th className="num">{t('details.col.avg-moves')}</th>
                <th className="num">{t('details.col.time')}</th>
                <th>{t('details.col.last')}</th>
              </tr>
            </thead>
            <tbody>
              {games.map((g) => (
                <tr key={g.game_type} data-game={g.game_type}>
                  <td>
                    <i className="details_dot" style={{ background: colorOf.get(g.game_type) ?? '#8a8a8a' }} />
                    {gameName(g.game_type)}
                  </td>
                  <td className="num">{g.wins.toLocaleString()}</td>
                  <td className="num">{g.share.toFixed(1)}%</td>
                  <td className="num">{bestCell(g.best_time, formatWinTime)}</td>
                  <td className="num">{bestCell(g.best_score, (v) => v.toLocaleString())}</td>
                  <td className="num">{bestCell(g.best_moves, (v) => v.toLocaleString())}</td>
                  <td className="num">{formatWinTime(g.avg_time ?? 0)}</td>
                  <td className="num">{g.avg_moves ?? '-'}</td>
                  <td className="num">{hoursMinutes(g.sum_time)}</td>
                  <td>{g.last_win?.text ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="details_sub">{t('details.best-note')}</p>
      </section>
      {games.some((g) => g.rank) ? (
        <section className="details_box details_table_box">
          <h3>{t('details.head.places')}</h3>
          <div className="details_table_scroll">
            <table className="details_table" data-table="places">
              <thead>
                <tr>
                  <th>{t('details.col.game')}</th>
                  <th className="num">{t('details.place.score')}</th>
                  <th className="num">{t('details.place.time')}</th>
                  <th className="num">{t('details.place.moves')}</th>
                </tr>
              </thead>
              <tbody>
                {games
                  .filter((g) => g.rank)
                  .map((g) => (
                    <tr key={g.game_type} data-game={g.game_type}>
                      <td>
                        <i className="details_dot" style={{ background: colorOf.get(g.game_type) ?? '#8a8a8a' }} />
                        {gameName(g.game_type)}
                      </td>
                      <td className="num">{place(g, 'score') ?? '-'}</td>
                      <td className="num">{place(g, 'time') ?? '-'}</td>
                      <td className="num">{place(g, 'moves') ?? '-'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="details_sub">{t('details.place-note')}</p>
        </section>
      ) : null}
    </>
  );

  const recent = (
    <section className="details_box details_table_box">
      <h3>{t('details.head.recent')}</h3>
      <div className="details_table_scroll">
        <table className="details_table" data-table="recent">
          <thead>
            <tr>
              <th>{t('details.col.when')}</th>
              <th>{t('details.col.game')}</th>
              <th className="num">{t('winTimeHead').replace(':', '')}</th>
              <th className="num">{t('winScoresHead').replace(':', '')}</th>
              <th className="num">{t('winMovesHead').replace(':', '')}</th>
              <th>{t('details.col.kind')}</th>
            </tr>
          </thead>
          <tbody>
            {data.recent.map((w) => (
              <tr key={w.id}>
                <td>{w.created.text}</td>
                <td>
                  <a href={share(w.id)} target="_blank" rel="noreferrer">
                    {gameName(w.game_type)}
                  </a>
                </td>
                <td className="num">{formatWinTime(w.time)}</td>
                <td className="num">{w.score.toLocaleString()}</td>
                <td className="num">{w.moves.toLocaleString()}</td>
                <td>{w.is_retry_deal ? t('details.mix.retry') : t('details.mix.new')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <div className="details_panel" data-section={section}>
      {pills}
      <div className="details_body">
        {section === 'overview'
          ? overview
          : section === 'history'
            ? history
            : section === 'games'
              ? gamesSection
              : section === 'recent'
                ? recent
                : <OnlineActivity gameName={gameName} colorOf={colorOf} />}
      </div>
    </div>
  );
}
